import { cx } from '@vizit/theme';
import { Slider as AriaSlider, SliderThumb, SliderTrack } from 'react-aria-components';
import type { SliderProps as AriaSliderProps } from 'react-aria-components';
import { composeTailwindRenderProps } from '../../utils';

interface SliderProps extends Omit<AriaSliderProps<number>, 'children'> {
  /**
   * Accessible name of the slider. The slider renders no visible label, so it is required.
   *
   * @required
   */
  'aria-label': string;
}

/**
 * A single-thumb horizontal slider for picking a number in a range, without a visible label or value output. It fills
 * the width of its container, so wrap it to size it.
 *
 * @example
 * <Slider aria-label="Thumbnail size" minValue={3} maxValue={10} value={size} onChange={setSize} />
 */
function Slider(props: SliderProps) {
  const { className, ...other } = props;

  return (
    <AriaSlider
      {...other}
      className={composeTailwindRenderProps(className, 'flex w-full items-center data-[disabled]:opacity-50')}
    >
      {/* Inset by the thumb's radius so the thumb stays inside the slider at both ends of the range */}
      <SliderTrack className="relative mx-6 flex h-12 grow items-center">
        <div aria-hidden="true" className="absolute -inset-x-6 h-4 rounded-full bg-gray-utility-200" />
        <SliderThumb
          className={cx(
            'top-1/2 h-12 w-12 rounded-full border-2 border-brand-600 bg-white shadow-sm',
            'cursor-grab data-[dragging]:cursor-grabbing data-[disabled]:cursor-not-allowed',
            'outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-600 data-[focus-visible]:ring-offset-2',
          )}
        />
      </SliderTrack>
    </AriaSlider>
  );
}

export { Slider };
export type { SliderProps };
